import { cap } from '../utils/format';
import React, { useState, useEffect, useCallback } from 'react';
import { Monitor, Users, Ticket, Wrench, TrendingDown, CheckCircle, AlertCircle, Clock, Package, BarChart2, Zap, X, ChevronRight, ArrowRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from 'recharts';
import api from '../api/client';
import Badge from '../components/Badge';
import { useAuth } from '../context/AuthContext';
import CompanyDeptFilter from '../components/CompanyDeptFilter';
import PolicyStatusWidget from '../components/PolicyStatusWidget';

const CT = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'linear-gradient(135deg,#0d1528,#080f1e)', border: '1px solid rgba(59,130,246,0.4)', borderRadius: 12, padding: '10px 14px', boxShadow: '0 8px 32px rgba(0,0,0,0.7)', minWidth: 130 }}>
      {label && <div style={{ color: '#64748b', fontSize: 11, marginBottom: 6, fontWeight: 600 }}>{label}</div>}
      {payload.map((p, i) => <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: i < payload.length - 1 ? 4 : 0 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.fill || p.color || '#60a5fa', flexShrink: 0 }} />
        <span style={{ color: '#94a3b8', fontSize: 12 }}>{p.name}:</span>
        <span style={{ color: '#f1f5f9', fontSize: 12, fontWeight: 700 }}>{p.value}</span>
      </div>)}
    </div>
  );
};

// Clean row for popup — structured columns, no overlap
function PopupRow({ cols }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: cols.map(c => c.w || '1fr').join(' '),
      gap: '12px',
      alignItems: 'center',
      padding: '10px 14px',
      borderRadius: 10,
      borderBottom: '1px solid rgba(255,255,255,0.04)',
      transition: 'background 0.15s',
    }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.06)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
      {cols.map((col, i) => (
        <div key={i} style={{ overflow: 'hidden', minWidth: 0 }}>
          {col.label && <div style={{ fontSize: 10, color: '#334155', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{col.label}</div>}
          <div style={{ fontSize: 13, color: col.color || '#94a3b8', fontWeight: col.bold ? 600 : 400, fontFamily: col.mono ? 'monospace' : 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {col.value}
          </div>
          {col.sub && <div style={{ fontSize: 11, color: '#334155', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{col.sub}</div>}
        </div>
      ))}
    </div>
  );
}

function PopupHeader({ cols }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: cols.map(c => c.w || '1fr').join(' '),
      gap: '12px',
      padding: '8px 14px 6px',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      marginBottom: 4,
    }}>
      {cols.map((col, i) => (
        <div key={i} style={{ fontSize: 10, fontWeight: 700, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {col.header || ''}
        </div>
      ))}
    </div>
  );
}

function TilePopup({ config, onClose }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!config) return;
    setLoading(true);
    api.get(config.endpoint, { params: { ...config.params, limit: 20 } })
      .then(r => setData(r.data.data || r.data.tickets || r.data.repairs || r.data.damages || []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [config]);

  if (!config) return null;

  const cols = config.cols || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ animation: 'fadeIn 0.2s ease-out' }}>
      <div className="absolute inset-0" style={{ background: 'rgba(4,8,16,0.88)', backdropFilter: 'blur(10px)' }} onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[82vh] flex flex-col rounded-2xl overflow-hidden"
        style={{ background: 'linear-gradient(160deg,#0d1528 0%,#090e1c 100%)', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 32px 80px rgba(0,0,0,0.85), 0 0 40px rgba(59,130,246,0.12)', animation: 'slideUp 0.3s ease-out' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${config.color}1a`, border: `1px solid ${config.color}35` }}>
              <config.icon size={17} style={{ color: config.color }} />
            </div>
            <div>
              <div className="font-bold" style={{ color: '#e2e8f0', fontSize: 15 }}>{config.title}</div>
              <div style={{ fontSize: 11, color: '#334155', marginTop: 1 }}>{config.subtitle}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {!loading && <span style={{ fontSize: 11, color: '#1e3a5f', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 20, padding: '2px 10px' }}>{data.length} records</span>}
            <button onClick={onClose} className="btn-ghost p-1.5"><X size={15} /></button>
          </div>
        </div>

        {/* Column headers */}
        {cols.length > 0 && !loading && data.length > 0 && <PopupHeader cols={cols} />}

        {/* Body */}
        <div className="overflow-y-auto flex-1" style={{ padding: '4px 6px 8px' }}>
          {loading ? (
            <div className="flex items-center justify-center h-36 gap-3" style={{ color: '#334155' }}>
              <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
              <span style={{ fontSize: 13 }}>Loading records...</span>
            </div>
          ) : data.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-36 gap-2" style={{ color: '#1e3a5f' }}>
              <span style={{ fontSize: 28 }}>—</span>
              <span style={{ fontSize: 13 }}>No records found</span>
            </div>
          ) : (
            data.map((row, i) => <PopupRow key={row.id || i} cols={config.getRow(row)} />)
          )}
        </div>

        {data.length >= 20 && (
          <div style={{ padding: '10px 14px', borderTop: '1px solid rgba(255,255,255,0.05)', fontSize: 11, textAlign: 'center', color: '#1e3a5f', flexShrink: 0 }}>
            Showing first 20 · Open the full module page to see all records
          </div>
        )}
      </div>
    </div>
  );
}

const KPI = ({ icon: Icon, label, value, sub, color, delay = 0, pulse, onClick }) => (
  <div onClick={onClick} className="card-3d glass glow-border rounded-2xl p-5 relative overflow-hidden cursor-pointer group"
    style={{ animation: `slideUp 0.5s ease-out ${delay}ms both` }}>
    <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full pointer-events-none" style={{ background: color, opacity: 0.08, filter: 'blur(16px)' }} />
    <div className="flex items-center justify-between mb-3">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}18`, border: `1px solid ${color}35` }}>
        <Icon size={16} style={{ color }} />
      </div>
      <div className="flex items-center gap-1.5">
        {pulse && <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: color }} />}
        <ChevronRight size={12} style={{ color: '#1e3a5f', opacity: 0 }} className="group-hover:opacity-100 transition-opacity" />
      </div>
    </div>
    <div className="text-2xl font-bold mb-0.5" style={{ color: '#f1f5f9' }}>{value ?? <span style={{ color: '#1e3a5f' }}>—</span>}</div>
    <div className="text-xs font-medium" style={{ color: '#475569' }}>{label}</div>
    {sub && <div className="text-xs mt-1" style={{ color: '#1e3a5f' }}>{sub}</div>}
    <div className="absolute bottom-2 right-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#334155' }}>click for details</div>
  </div>
);

// Asset row columns helper
const assetCols = [
  { header: 'Asset #', w: '140px' },
  { header: 'Brand / Model', w: '1fr' },
  { header: 'Holder', w: '140px' },
  { header: 'Status', w: '100px' },
];
const getAssetRow = (r, statusOverride) => [
  { value: r.asset_number, color: '#60a5fa', mono: true, bold: true, w: '140px' },
  { value: `${r.brand || ''} ${r.model || ''}`.trim() || '—', color: '#e2e8f0', sub: r.Company?.code || '', w: '1fr' },
  { value: r.currentHolder?.full_name || '—', color: '#94a3b8', w: '140px' },
  { value: <Badge value={statusOverride || r.status} />, w: '100px' },
];

const POPUP_CONFIGS = {
  total_assets: {
    title: 'All Assets', subtitle: 'Complete asset inventory', color: '#3b82f6', icon: Monitor,
    endpoint: '/assets', params: {}, cols: assetCols,
    getRow: r => getAssetRow(r),
  },
  in_use: {
    title: 'Assets In Use', subtitle: 'Currently assigned to employees', color: '#3b82f6', icon: Monitor,
    endpoint: '/assets', params: { status: 'in_use' }, cols: assetCols,
    getRow: r => getAssetRow(r),
  },
  available: {
    title: 'Available Assets', subtitle: 'Ready to be allocated', color: '#14b8a6', icon: CheckCircle,
    endpoint: '/assets', params: { status: 'available' },
    cols: [{ header: 'Asset #', w: '140px' }, { header: 'Brand / Model', w: '1fr' }, { header: 'Category', w: '120px' }, { header: 'Company', w: '80px' }],
    getRow: r => [
      { value: r.asset_number, color: '#2dd4bf', mono: true, bold: true, w: '140px' },
      { value: `${r.brand || ''} ${r.model || ''}`.trim() || '—', color: '#e2e8f0', w: '1fr' },
      { value: <Badge value={r.category} />, w: '120px' },
      { value: r.Company?.code || '—', color: '#475569', w: '80px' },
    ],
  },
  in_repair: {
    title: 'Assets In Repair', subtitle: 'Currently under repair', color: '#8b5cf6', icon: Clock,
    endpoint: '/assets', params: { status: 'in_repair' }, cols: assetCols,
    getRow: r => getAssetRow(r),
  },
  broken: {
    title: 'Broken / Lost Assets', subtitle: 'Assets out of service', color: '#ef4444', icon: AlertCircle,
    endpoint: '/assets', params: { status: 'broken' }, cols: assetCols,
    getRow: r => getAssetRow(r, r.status),
  },
  depreciated: {
    title: 'Deprecated Assets', subtitle: 'Assets past 4-year lifecycle', color: '#f97316', icon: TrendingDown,
    endpoint: '/assets', params: { depreciation_status: 'depreciated' },
    cols: [{ header: 'Asset #', w: '140px' }, { header: 'Brand / Model', w: '1fr' }, { header: 'Purchased', w: '110px' }, { header: 'Age', w: '80px' }],
    getRow: r => {
      const age = r.purchased_date ? (() => {
        const days = Math.floor((Date.now() - new Date(r.purchased_date)) / 86400000);
        return days > 365 ? `${Math.floor(days/365)}y ${Math.floor((days%365)/30)}mo` : `${Math.floor(days/30)}mo`;
      })() : '—';
      return [
        { value: r.asset_number, color: '#fb923c', mono: true, bold: true, w: '140px' },
        { value: `${r.brand || ''} ${r.model || ''}`.trim() || '—', color: '#e2e8f0', w: '1fr' },
        { value: r.purchased_date || '—', color: '#475569', w: '110px' },
        { value: age, color: '#f97316', bold: true, w: '80px' },
      ];
    },
  },
  open_tickets: {
    title: 'In Progress Tickets', subtitle: 'Tickets actively being worked on', color: '#818cf8', icon: Ticket,
    endpoint: '/tickets', params: { status: 'in_progress' },
    cols: [{ header: 'Ticket #', w: '110px' }, { header: 'Title', w: '1fr' }, { header: 'Reported By', w: '140px' }, { header: 'Priority', w: '90px' }],
    getRow: r => [
      { value: r.ticket_number || '—', color: '#60a5fa', mono: true, bold: true, w: '110px' },
      { value: r.title || '—', color: '#e2e8f0', w: '1fr' },
      { value: r.reportedBy?.full_name || '—', color: '#94a3b8', w: '140px' },
      { value: <Badge value={r.priority} />, w: '90px' },
    ],
  },
  repairs_active: {
    title: 'Active Repairs', subtitle: 'Pending and in-progress repairs', color: '#ef4444', icon: Wrench,
    endpoint: '/repairs', params: {},
    cols: [{ header: 'Issue', w: '1fr' }, { header: 'Vendor', w: '140px' }, { header: 'Date', w: '100px' }, { header: 'Status', w: '100px' }],
    getRow: r => [
      { value: r.issue_description || '—', color: '#e2e8f0', w: '1fr' },
      { value: r.repair_vendor || 'No vendor', color: '#94a3b8', w: '140px' },
      { value: r.reported_date || '—', color: '#475569', w: '100px' },
      { value: <Badge value={r.status} />, w: '100px' },
    ],
  },
  in_store: {
    title: 'Assets In Store', subtitle: 'Items in the store/inventory', color: '#06b6d4', icon: Package,
    endpoint: '/assets', params: { status: 'in_store' }, cols: assetCols,
    getRow: r => [
      { value: r.asset_number, color: '#22d3ee', mono: true, bold: true, w: '140px' },
      { value: `${r.brand || ''} ${r.model || ''}`.trim() || '—', color: '#e2e8f0', w: '1fr' },
      { value: r.Company?.code || '—', color: '#475569', w: '140px' },
      { value: <Badge value={r.category} />, w: '100px' },
    ],
  },
  acc_total: {
    title: 'All Accessories', subtitle: 'Complete accessory inventory', color: '#c084fc', icon: Package,
    endpoint: '/accessories', params: {},
    cols: [{ header: 'ACC #', w: '120px' }, { header: 'Name', w: '1fr' }, { header: 'Category', w: '130px' }, { header: 'Status', w: '100px' }],
    getRow: r => [
      { value: r.accessory_number || '—', color: '#a78bfa', mono: true, bold: true, w: '120px' },
      { value: r.name || `${r.brand||''} ${r.model||''}`.trim() || '—', color: '#e2e8f0', w: '1fr' },
      { value: r.category?.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase()) || '—', color: '#94a3b8', w: '130px' },
      { value: <Badge value={r.status}/>, w: '100px' },
    ],
  },
  acc_in_use: {
    title: 'Accessories In Use', subtitle: 'Currently assigned to employees', color: '#3b82f6', icon: CheckCircle,
    endpoint: '/accessories', params: { status: 'in_use' },
    cols: [{ header: 'ACC #', w: '120px' }, { header: 'Name', w: '1fr' }, { header: 'Category', w: '130px' }, { header: 'Holder', w: '140px' }],
    getRow: r => [
      { value: r.accessory_number || '—', color: '#60a5fa', mono: true, bold: true, w: '120px' },
      { value: r.name || `${r.brand||''} ${r.model||''}`.trim() || '—', color: '#e2e8f0', w: '1fr' },
      { value: r.category?.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase()) || '—', color: '#94a3b8', w: '130px' },
      { value: r.assignedEmployee?.full_name || '—', color: '#475569', w: '140px' },
    ],
  },
  acc_available: {
    title: 'Available Accessories', subtitle: 'Ready to be allocated', color: '#10b981', icon: CheckCircle,
    endpoint: '/accessories', params: { status: 'available' },
    cols: [{ header: 'ACC #', w: '120px' }, { header: 'Name', w: '1fr' }, { header: 'Category', w: '130px' }, { header: 'Brand/Model', w: '140px' }],
    getRow: r => [
      { value: r.accessory_number || '—', color: '#2dd4bf', mono: true, bold: true, w: '120px' },
      { value: r.name || '—', color: '#e2e8f0', w: '1fr' },
      { value: r.category?.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase()) || '—', color: '#94a3b8', w: '130px' },
      { value: `${r.brand||''} ${r.model||''}`.trim() || '—', color: '#475569', w: '140px' },
    ],
  },
  acc_disposed: {
    title: 'Disposed Accessories', subtitle: 'Derecognized accessories', color: '#f87171', icon: AlertCircle,
    endpoint: '/accessories', params: { status: 'disposed' },
    cols: [{ header: 'ACC #', w: '120px' }, { header: 'Name', w: '1fr' }, { header: 'Category', w: '130px' }, { header: 'Status', w: '100px' }],
    getRow: r => [
      { value: r.accessory_number || '—', color: '#f87171', mono: true, bold: true, w: '120px' },
      { value: r.name || `${r.brand||''} ${r.model||''}`.trim() || '—', color: '#e2e8f0', w: '1fr' },
      { value: r.category?.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase()) || '—', color: '#94a3b8', w: '130px' },
      { value: <Badge value={r.status}/>, w: '100px' },
    ],
  },
};

// Recent handovers widget — defined at module level
function RecentHandovers() {
  const [handovers, setHandovers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/reports/summary')
      .then(r => setHandovers(r.data.recentHandovers || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || handovers.length === 0) return null;

  return (
    <div className="glass glow-border rounded-2xl p-5" style={{ animation: 'slideUp 0.5s ease-out 0.8s both' }}>
      <div className="flex items-center gap-2 mb-4">
        <ArrowRight size={14} style={{ color: '#34d399' }} />
        <span className="text-sm font-semibold" style={{ color: '#94a3b8' }}>Recent Asset Handovers (Last 30 Days)</span>
      </div>
      <div className="overflow-x-auto">
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              {['Asset #', 'Brand / Model', 'Category', 'Handed To', 'Handover Date', 'Returned'].map(h => (
                <th key={h} style={{ padding: '6px 12px', textAlign: 'left', color: '#334155', fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {handovers.slice(0, 10).map((h, i) => (
              <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(52,211,153,0.04)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#60a5fa' }}>{h.asset_number}</td>
                <td style={{ padding: '8px 12px', color: '#e2e8f0' }}>{h.brand} {h.model}</td>
                <td style={{ padding: '8px 12px', color: '#475569', textTransform: 'capitalize' }}>{h.category?.replace('_', ' ')}</td>
                <td style={{ padding: '8px 12px', color: '#94a3b8' }}>{h.employee_name}</td>
                <td style={{ padding: '8px 12px', color: '#34d399' }}>{h.assigned_date}</td>
                <td style={{ padding: '8px 12px', color: h.returned_date ? '#f59e0b' : '#1e3a5f' }}>{h.returned_date || 'Still holding'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { isViewOnly, isAdmin, isDirector, isFinance } = useAuth();
  const showCompanyFilter = isAdmin || isDirector || isFinance;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [popup, setPopup] = useState(null);
  const [itResponseStats, setItResponseStats] = useState([]);
  const [filterCompany, setFilterCompany] = useState(null);
  const [filterDept, setFilterDept] = useState(null);

  const loadDashboard = useCallback(() => {
    const params = {};
    if (filterCompany?.length) params.company_id = Array.isArray(filterCompany) ? filterCompany.join(",") : filterCompany;
    if (filterDept) params.department_id = filterDept;
    api.get('/dashboard', { params }).then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
    if (!isViewOnly) {
      api.get('/dashboard/it-response-stats').then(r => setItResponseStats(r.data || [])).catch(() => {});
    }
  }, [filterCompany, filterDept, isViewOnly]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const openPopup = useCallback((key) => {
    const config = POPUP_CONFIGS[key];
    if (config) setPopup(config);
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center animate-pulse-glow" style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)' }}>
          <BarChart2 size={20} style={{ color: '#60a5fa' }} />
        </div>
        <div className="text-sm" style={{ color: '#334155' }}>Loading dashboard...</div>
      </div>
    </div>
  );

  const assets = data?.assets || {};
  const tickets = data?.tickets || {};
  const repairs = data?.repairs || {};
  const employees = data?.employees || {};
  const accessories = data?.accessories || {};
  const depreciated = parseInt(assets.depreciated || 0);
  const depPct = assets.total_assets > 0 ? Math.round((depreciated / parseInt(assets.total_assets)) * 100) : 0;

  const categoryData = (data?.categoryBreakdown || []).map(c => ({ name: c.category?.replace(/_/g, ' '), count: parseInt(c.count || 0) })).filter(c => c.count > 0);
  const statusData = [
    { name: 'In Use', value: parseInt(assets.in_use || 0), color: '#3b82f6' },
    { name: 'Available', value: parseInt(assets.available || 0), color: '#10b981' },
    { name: 'In Repair', value: parseInt(assets.in_repair || 0), color: '#f59e0b' },
    { name: 'Broken', value: parseInt(assets.broken || 0), color: '#ef4444' },
    { name: 'Deprecated', value: depreciated, color: '#f97316' },
  ].filter(d => d.value > 0);

  const ax = { fill: '#1e3a5f', fontSize: 10 };

  return (
    <div className="space-y-6">
      <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold gradient-text">Dashboard</h1>
            <p className="text-xs mt-0.5" style={{ color: '#1e3a5f' }}>Click any card to see detailed records · CL Synergy Group IT Assets</p>
          </div>
          {showCompanyFilter && (
            <CompanyDeptFilter
              companyId={filterCompany}
              deptId={filterDept}
              onChange={(c, d) => { setFilterCompany(c); setFilterDept(d); }}
            />
          )}
        </div>
      </div>

      {/* Policy acknowledgement status — all users */}
      <PolicyStatusWidget />

      {/* Row 1 — IT-only tiles hidden from Director/Finance */}
      <div className={`grid gap-4 ${isViewOnly ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-4'}`}>
        <KPI icon={Monitor} label="Total Assets" value={assets.total_assets} sub={`${assets.in_use || 0} in use`} color="#3b82f6" delay={0} onClick={() => openPopup('total_assets')} />
        <KPI icon={Users} label="Employees" value={employees.active} sub={`${employees.inactive || 0} inactive`} color="#10b981" delay={60} />
        {!isViewOnly && <KPI icon={Ticket} label="In Progress Tickets" value={tickets.in_progress || 0} sub={parseInt(tickets.critical_open) > 0 ? `⚠ ${tickets.critical_open} critical` : 'Being worked on'} color="#818cf8" delay={120} onClick={() => openPopup('open_tickets')} />}
        {!isViewOnly && <KPI icon={Wrench} label="Repairs Active" value={parseInt(repairs.pending || 0) + parseInt(repairs.in_progress || 0)} sub={`${repairs.total || 0} total`} color="#ef4444" delay={180} onClick={() => openPopup('repairs_active')} />}
      </div>

      {/* Row 2 — Available / Deprecated / Broken / In Repair */}
      <div className={`grid gap-4 ${isViewOnly ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-4'}`}>
        {!isViewOnly && <KPI icon={CheckCircle} label="Available" value={assets.available} sub="Ready to allocate" color="#14b8a6" delay={0} onClick={() => openPopup('available')} />}
        <KPI icon={TrendingDown} label="Deprecated" value={depreciated} sub={`${depPct}% of assets`} color="#f97316" delay={isViewOnly ? 0 : 60} onClick={() => openPopup('depreciated')} />
        {!isViewOnly && <KPI icon={AlertCircle} label="Broken / Lost" value={parseInt(assets.broken || 0) + parseInt(assets.lost || 0)} color="#ef4444" delay={120} onClick={() => openPopup('broken')} />}
        {!isViewOnly && <KPI icon={Clock} label="In Repair" value={assets.in_repair} color="#8b5cf6" delay={180} onClick={() => openPopup('in_repair')} />}
      </div>

      {/* Charts — Asset Status pie hidden from Director/Finance */}
      <div className={`grid grid-cols-1 gap-6 ${!isViewOnly ? 'lg:grid-cols-3' : ''}`}>
        <div className={`${!isViewOnly ? 'lg:col-span-2' : ''} glass glow-border rounded-2xl p-5`} style={{ animation: 'slideUp 0.5s ease-out 0.2s both' }}>
          <div className="text-sm font-semibold mb-4" style={{ color: '#94a3b8' }}>Assets by Category</div>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={categoryData} margin={{ left: -15, right: 5 }}>
                <defs><linearGradient id="barG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9}/><stop offset="100%" stopColor="#1d4ed8" stopOpacity={0.7}/></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" />
                <XAxis dataKey="name" tick={ax} /><YAxis tick={ax} />
                <Tooltip content={<CT />} />
                <Bar dataKey="count" fill="url(#barG)" radius={[5,5,0,0]} name="Count" />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-48 flex items-center justify-center text-sm" style={{ color: '#1e3a5f' }}>No data</div>}
        </div>

        {!isViewOnly && (
          <div className="glass glow-border rounded-2xl p-5" style={{ animation: 'slideUp 0.5s ease-out 0.3s both' }}>
            <div className="text-sm font-semibold mb-4" style={{ color: '#94a3b8' }}>Asset Status</div>
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={statusData} dataKey="value" cx="50%" cy="50%" innerRadius={52} outerRadius={80} paddingAngle={3}>
                    {statusData.map((d, i) => <Cell key={i} fill={d.color} stroke="transparent" />)}
                  </Pie>
                  <Tooltip content={<CT />} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="h-48 flex items-center justify-center text-sm" style={{ color: '#1e3a5f' }}>No data</div>}
            <div className="space-y-1 mt-2">
              {statusData.map((d, i) => (
                <div key={i} className="flex items-center justify-between text-xs cursor-pointer" onClick={() => openPopup(d.name.toLowerCase().replace(/ /g,'_') === 'in_use' ? 'in_use' : d.name.toLowerCase().replace(/ \//g,'').replace(/ /g,'_'))}>
                  <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full" style={{ background: d.color }}/><span style={{ color: '#475569' }}>{d.name}</span></div>
                  <span className="font-medium hover:underline" style={{ color: d.color }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Depreciation progress */}
      {depreciated > 0 && (
        <div className="glass rounded-2xl p-4 flex items-start gap-3 cursor-pointer" style={{ border: '1px solid rgba(249,115,22,0.25)', background: 'rgba(249,115,22,0.05)' }} onClick={() => openPopup('depreciated')}>
          <TrendingDown size={16} style={{ color: '#fb923c', flexShrink: 0, marginTop: 2 }} />
          <div className="flex-1">
            <div className="text-sm font-semibold" style={{ color: '#fb923c' }}>{depreciated} Assets Deprecated — Click to Review</div>
            <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div className="h-full rounded-full" style={{ width: `${depPct}%`, background: 'linear-gradient(90deg,#f97316,#ef4444)' }} />
            </div>
          </div>
          <span className="text-sm font-bold" style={{ color: '#f97316' }}>{depPct}%</span>
        </div>
      )}

      {/* Accessories Overview */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Package size={14} style={{ color: '#c084fc' }}/>
          <span className="text-sm font-semibold" style={{ color: '#94a3b8' }}>Accessories Overview</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI icon={Package} label="Total Accessories" value={accessories.total} color="#c084fc" delay={0} onClick={() => openPopup('acc_total')} />
          <KPI icon={CheckCircle} label="In Use" value={accessories.in_use} color="#3b82f6" delay={60} onClick={() => openPopup('acc_in_use')} />
          <KPI icon={CheckCircle} label="Available" value={accessories.available} color="#10b981" delay={120} onClick={() => openPopup('acc_available')} />
          <KPI icon={AlertCircle} label="Disposed" value={accessories.disposed} color="#f87171" delay={180} onClick={() => openPopup('acc_disposed')} />
        </div>
      </div>

      {/* Recent Activity — admin only */}
      {isAdmin && (
      <div className="glass glow-border rounded-2xl p-5" style={{ animation: 'slideUp 0.5s ease-out 0.7s both' }}>
        <div className="flex items-center gap-2 mb-4">
          <Zap size={14} style={{ color: '#818cf8' }} />
          <span className="text-sm font-semibold" style={{ color: '#94a3b8' }}>Recent Activity</span>
        </div>
        {data?.recentActivity?.length > 0 ? (
          <div>
            {data.recentActivity.map((item, i) => (
              <div key={i} className="flex items-center gap-3 py-2.5 px-2 rounded-xl cursor-default transition-colors"
                style={{ borderBottom: i < data.recentActivity.length - 1 ? '1px solid rgba(255,255,255,0.03)' : 'none' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(99,102,241,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0" style={{ background: 'linear-gradient(135deg,rgba(99,102,241,0.2),rgba(59,130,246,0.2))', color: '#818cf8', border: '1px solid rgba(99,102,241,0.2)' }}>
                  {item.full_name?.charAt(0) || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs" style={{ color: '#94a3b8' }}>
                    <span className="font-semibold" style={{ color: '#cbd5e1' }}>{item.full_name || 'System'}</span>
                    {' · '}<span className="capitalize">{item.action}</span>{' in '}
                    <span style={{ color: '#818cf8' }} className="capitalize">{item.module?.replace('_', ' ')}</span>
                  </span>
                </div>
                <div className="text-xs flex-shrink-0 tabular-nums" style={{ color: '#152040' }}>
                  {new Date(item.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
          </div>
        ) : <div className="text-sm py-6 text-center" style={{ color: '#1e3a5f' }}>No recent activity</div>}
      </div>
      )}

      {/* Recent Asset Handovers — admin only */}
      {isAdmin && <RecentHandovers/>}

      {/* IT Staff Avg Response Time — admin sees all IT staff; IT staff sees own row only */}
      {!isViewOnly && itResponseStats.length > 0 && (
        <div className="glass glow-border rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Clock size={14} style={{ color: '#06b6d4' }}/>
            <span className="text-sm font-semibold" style={{ color: '#94a3b8' }}>IT Staff Avg Response Time</span>
          </div>
          <div className="space-y-2">
            {itResponseStats.map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold"
                  style={{ background: 'rgba(6,182,212,0.12)', color: '#06b6d4', border: '1px solid rgba(6,182,212,0.25)' }}>
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{s.staff_name}</span>
                    <span className="text-sm font-bold" style={{ color: s.avg_hours < 4 ? '#34d399' : s.avg_hours < 24 ? '#f59e0b' : '#f87171' }}>
                      {s.avg_hours}h avg
                    </span>
                  </div>
                  <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div className="h-full rounded-full"
                      style={{ width: `${Math.min(100, (s.avg_hours / 48) * 100)}%`,
                               background: s.avg_hours < 4 ? 'linear-gradient(90deg,#10b981,#34d399)' : s.avg_hours < 24 ? 'linear-gradient(90deg,#f59e0b,#fbbf24)' : 'linear-gradient(90deg,#ef4444,#f87171)' }}/>
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: '#334155' }}>{s.total_resolved} tickets resolved</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {popup && <TilePopup config={popup} onClose={() => setPopup(null)} />}
    </div>
  );
}
